import { Icon } from '@design-system';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo } from 'react';

import { Image } from '../../../../components/Image';
import { useDialogContext } from '../../../../hooks/useDialogContext';
import { GeneratedPrivateLink } from '../../../../types/generated';
import { AnalyticsObjectProvider } from '../../../../utils/analytics/object';
import { PrivateSalesController } from '../../types/PrivateLink';
import { PasswordForm } from './PasswordForm';

type Props = {
  privateLink: GeneratedPrivateLink;
  privateSalesController: PrivateSalesController;
};

export const UnlockForm: React.FC<Props> = ({
  privateLink,
  privateSalesController,
}) => {
  const { heading, imageUrl, passwordRequired, strapline } = privateLink;
  const { hideDialog, showDialog } = useDialogContext();

  const { t } = useTranslation('private');
  const content = useMemo(
    () => (
      <AnalyticsObjectProvider
        name="unlock page"
        properties={{
          'private_status': 'open',
          'unlock_mechanism': passwordRequired ? 'code' : 'open',
        }}
        sendViewedEvent
      >
        {(emitter) => (
          <main className="theme-light max-w-md rounded-t-lg bg-white text-neutral-6 sm:h-auto sm:rounded-lg">
            <div className="flex flex-col items-center gap-6 rounded-t-lg bg-white p-6 pt-[76px] sm:rounded-lg">
              <h1 className="text-center text-md font-bold md:text-lg">
                {t('unlock.title')}
              </h1>

              <div className="relative h-24 w-24 overflow-hidden rounded-full md:h-32 md:w-32">
                <Image alt={heading} src={imageUrl} width={128} />
                <div className="absolute inset-0 flex items-center justify-center bg-neutral-6/50">
                  <Icon className="text-white" name="ic_lock" size="md" />
                </div>
              </div>

              <p className="text-balance text-center">{strapline}</p>

              {passwordRequired ? (
                <PasswordForm privateSalesController={privateSalesController} />
              ) : null}
            </div>
            <div className="px-6 py-5 text-center text-neutral-6">
              {passwordRequired ? t('unlock.help') : null}

              <a
                className="hyperlink"
                href="mailto:info@avantarte.com"
                onClick={() => {
                  emitter('get in touch', 'clicked');
                }}
              >
                {passwordRequired && t('unlock.helpLink')}
              </a>
            </div>
          </main>
        )}
      </AnalyticsObjectProvider>
    ),
    [heading, imageUrl, passwordRequired, privateSalesController, strapline, t],
  );

  useEffect(() => {
    showDialog(content, { dismissible: false });
  }, [content]);

  useEffect(() => {
    return () => {
      hideDialog();
    };
  }, []);

  return null;
};
